import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Trans, useLingui } from '@lingui/react/macro';

// Services
import { AUTH_ERROR_CODES, isAccountCollisionError, useAuthContext } from '../../providers/auth.provider';
import { useUserContext, useUserResolved } from '../../providers/user/user.provider';
import { captureException } from '../../analytics/sentry';

// Utils
import { validateEmail } from '../../utils';
import * as ids from './testids';
import { ROUTE_PATHS } from '../_pages/_utils/router.utils';
import useSnackBar from '../Snackbar/useSnackBar';
import { getAuthenticationErrorMsg } from '../../utils/user.utils';
import { recordEvent } from '../../services/user.service';
import { showEmailSignIn } from '../../env';

// Components
import { GoogleButton, PhoneButton, AppleButton, EmailButton } from '../AuthButton';
import { PhoneField } from '../_core/forms/phone-input.component';
import { Modal } from '../_core/ui/modal.component';
import { InAppBrowserNotice } from '../_core/ui/in-app-browser-notice.component';
import { Capacitor } from '@capacitor/core';
import { Input, PasswordInput } from '../ui/input';
import { Button } from '../ui/button';
import { LoadingPlaceholder } from '../ui/LoadingPlaceholder';
import { RHFTextInput } from '../inputs/text-input';

export interface ISignIn {
  additionalTitle?: string;
  className?: string;
}

const SIGN_IN_FORM = {
  EMAIL_PASSWORD: 'EMAIL_PASSWORD',
  PHONE: 'PHONE',
};

const SignIn: React.FC<ISignIn> = ({ additionalTitle, className }) => {
  const { addMessage } = useSnackBar();
  const { t, i18n } = useLingui();

  const {
    handleSignIn,
    handleGoogleAuthSignIn,
    handleAppleAuthSignIn,
    error,
    setError,
    requestOTP,
    setRequestOTP,
    handleVerifyPhoneNumber,
    handleConfirmPhoneNumber,
    handelCheckAuthMethod,
    initialized: authInitialized,
    loading: authLoading,
    firebaseUser,
    handleDeleteUser,
    destroyRecaptcha,
    initializeRecaptcha,
  } = useAuthContext();

  const { loading: userLoading, initialized: userInitialized, user } = useUserContext();
  const userResolved = useUserResolved();

  const [signInForm, setSignInForm] = useState(SIGN_IN_FORM.PHONE);
  const [hasSubmittedPhone, setHasSubmittedPhone] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [attemptLeft, setAttemptLeft] = useState(2);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [showNoAccountModal, setShowNoAccountModal] = useState(false);

  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    handleSubmit,
    register,
    watch,
    control,
    formState: { errors, submitCount },
    getValues,
    setValue,
  } = useForm({ mode: 'onSubmit', reValidateMode: 'onSubmit' });

  const [showAltPrompt, setShowAltPrompt] = useState(false);
  const [isPhoneTouched, setIsPhoneTouched] = useState(false);
  const setPhoneInputTouched = () => {
    setIsPhoneTouched(true);
  };

  const emailError = t`Please enter a valid email address.`;
  const passwordError = t`Password must be at least 8 characters long.`;
  const watchedEmail = watch('email');
  const watchedPassword = watch('password');
  const watchedOTP = watch('otp');

  const errEccountExists =
    error === AUTH_ERROR_CODES.GOOGLE_FAIL_EMAIL_PASSWORD || error === AUTH_ERROR_CODES.EMAIL_PASSWORD_FAIL_GOOGLE;

  // Helper to check if firebaseUser is Google/Apple
  const isSocialProvider = (firebaseUser) => {
    if (!firebaseUser || !firebaseUser.providerData) return false;
    return firebaseUser.providerData.some((p) => p.providerId === 'google.com' || p.providerId === 'apple.com');
  };

  React.useEffect(() => {
    setDisabled(false);
    destroyRecaptcha();

    if (signInForm === SIGN_IN_FORM.PHONE && containerRef.current) {
      initializeRecaptcha('recaptcha-sign-in');
    }
  }, [signInForm, containerRef]);

  React.useEffect(() => {
    switch (error) {
      case AUTH_ERROR_CODES.PHONE_VERIFICATION_FAILED:
        addMessage(t`Phone verification failed. Please try again.`, false, 5000);
        destroyRecaptcha();
        setDisabled(false);
        setSubmitting(false);
        setError(null);
        break;
    }
  }, [error]);

  React.useEffect(() => {
    // If user is signed in with Google/Apple, but no user record exists, show error and delete adhoc account
    const handleSocialLoginError = async () => {
      if (
        firebaseUser &&
        !user &&
        isSocialProvider(firebaseUser) &&
        authInitialized &&
        userInitialized &&
        !userLoading &&
        !authLoading
      ) {
        addMessage(
          t`Sorry, this is not your original sign-in method. Most users use phone. Contact support@solve.finance if you need help recovering your initial log-in.`,
          false,
          5000,
        );
        await handleDeleteUser();
      }
    };
    handleSocialLoginError();
  }, [firebaseUser, user, authInitialized, userInitialized, userLoading, authLoading]);

  React.useEffect(() => {
    if (requestOTP) setResendCountdown(30);
  }, [requestOTP]);

  React.useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const onClick = async ({ email, phone, password }: { email?: string; phone?: string; password?: string }) => {
    try {
      switch (signInForm) {
        case SIGN_IN_FORM.PHONE:
          if (phone?.length) {
            setDisabled(true);
            try {
              const checkAuth = await handelCheckAuthMethod('phone', phone);
              if (!!checkAuth && !!checkAuth.found) {
                if (checkAuth.authMethod === 'email') {
                  addMessage(
                    t`This phone number is associated with an email account. Please sign in with your email and password.`,
                    false,
                  );
                  setDisabled(false);
                  setSignInForm(SIGN_IN_FORM.EMAIL_PASSWORD);
                  break;
                } else if (checkAuth.authMethod === 'other') {
                  addMessage(
                    t`This phone number is associated with another account. Please sign in with Google or Apple.`,
                    false,
                  );
                  setDisabled(false);
                  break;
                }
                // authMethod === 'phone' — proceed to send OTP
              }
            } catch {
              // Account not found
              setDisabled(false);
              setShowNoAccountModal(true);
              break;
            }
            await handleVerifyPhoneNumber(phone, hasSubmittedPhone);
            setRequestOTP(true);
            setDisabled(false);
            setHasSubmittedPhone(true);
          }
          break;
        case SIGN_IN_FORM.EMAIL_PASSWORD:
          if (email?.length) {
            setSubmitting(true);
            try {
              const checkAuth = await handelCheckAuthMethod('email', email);
              if (!!checkAuth && !!checkAuth.found) {
                if (checkAuth.authMethod === 'phone') {
                  addMessage(
                    t`This email address is already associated with a phone number. Please sign in with your phone number.`,
                    false,
                  );
                  setSubmitting(false);
                  setRequestOTP(false);
                  setSignInForm(SIGN_IN_FORM.PHONE);
                  break;
                } else if (checkAuth.authMethod === 'other') {
                  addMessage(
                    t`This email address is already associated with another account. Please sign in with your Google or Apple account`,
                    false,
                  );
                  setSubmitting(false);
                  setRequestOTP(false);
                  break;
                }
              }
            } catch (error) {
              addMessage(
                t`We were unable to find an account with that email address. Please sign up for a new account.`,
                false,
              );
              setSubmitting(false);
              break;
            }
            await handleSignIn(email, password);
            setSubmitting(false);
          }
          break;
      }
    } catch (e) {
      const errMsg = getAuthenticationErrorMsg(e);
      if (signInForm === SIGN_IN_FORM.EMAIL_PASSWORD) setShowAltPrompt(true);

      addMessage(errMsg ? i18n._(errMsg) : t`Solve service is unavailable, please try again later`, false, 5000);

      setSubmitting(false);
      setDisabled(false);
    }
  };

  const handlePhoneSubmit = async () => {
    const otp = getValues('otp');
    setSubmitting(true);
    setDisabled(true);
    setValue('otp', '');

    let accountCheck: 'existing' | 'new' | 'unknown' = 'unknown';

    try {
      const phone = getValues('phone');
      const checkAuth = await handelCheckAuthMethod('phone', phone);
      accountCheck = checkAuth?.found ? 'existing' : 'new';

      if (!!checkAuth && !!checkAuth.found) {
        if (checkAuth.authMethod === 'email') {
          addMessage(
            t`This phone number is already associated with an email address. Please sign in with your email and password.`,
            false,
          );
          setSubmitting(false);
          setDisabled(false);
          setRequestOTP(false);
          setSignInForm(SIGN_IN_FORM.EMAIL_PASSWORD);
          return;
        } else if (checkAuth.authMethod === 'other') {
          addMessage(
            t`This phone number is already associated with another account. Please sign in with other methods`,
            false,
          );
          setSubmitting(false);
          setDisabled(false);
          setRequestOTP(false);
          return;
        }
      }
    } catch {
      // Check failed — proceed and let Firebase handle it
    }

    if (attemptLeft == 0) {
      addMessage(t`Maximum attempts reached. Please re-enter your phone number and request a new code.`, false);
      setAttemptLeft(2);
      setDisabled(false);
      setSubmitting(false);
      setRequestOTP(false);
      return;
    }

    try {
      await handleConfirmPhoneNumber(otp, true);

      if (!Capacitor.isNativePlatform()) {
        recordEvent({
          eventName: accountCheck === 'new' ? 'Sign Up' : 'Sign In',
          firebaseUser,
          props: { type: 'PHONE', origin: 'SIGN_IN', accountCheck },
        });
      }

      setRequestOTP(false);
      setSubmitting(false);
      setDisabled(false);
    } catch (error) {
      if (error.code === 'auth/invalid-verification-code') {
        addMessage(t`Code incorrect. Please try again. You have ${attemptLeft} attempt(s) left`, false);
        setAttemptLeft(attemptLeft - 1);
        setRequestOTP(true);
      } else if (error.code === 'auth/code-expired') {
        addMessage(t`Your code has expired. Please request a new one.`, false);
        setRequestOTP(true);
      } else {
        addMessage(t`We were unable to verify your phone number. Please try again`, false);
        setRequestOTP(false);
      }

      setDisabled(false);
      setSubmitting(false);
    }
  };

  // On web the social providers redirect away and errors come back through handleAuthRedirect, but
  // on native they throw straight out of the button handler. Left unawaited that surfaced as an
  // unhandled rejection in Sentry and nothing at all on screen — most often
  // `auth/account-exists-with-different-credential`, i.e. this email already signed up another way.
  const handleSocialSignIn = async (signIn: () => Promise<void>) => {
    try {
      await signIn();
    } catch (e) {
      if (isAccountCollisionError(e)) {
        // Opens the "an account exists with that email" modal.
        setError(AUTH_ERROR_CODES.GOOGLE_FAIL_EMAIL_PASSWORD);
        return;
      }

      captureException(e, { flow: 'social-sign-in' });

      const message = getAuthenticationErrorMsg(e);

      addMessage(
        message
          ? i18n._(message)
          : t`An unknown error has occurred. Please try again or reach out to support for assistance`,
        false,
        5000,
      );
    }
  };

  // Sign-in succeeded but we don't know yet whether this user has an account —
  // hold here instead of rendering a live form (or guessing a destination) for
  // the frames it takes the lookup to land.
  if (firebaseUser && !firebaseUser.isAnonymous && !userResolved) {
    return <LoadingPlaceholder title={t`Signing you in...`} />;
  }

  if (user?.hasBureauAuth) return <Navigate to={ROUTE_PATHS.DASHBOARD} />;
  if (user) return <Navigate to={ROUTE_PATHS.CONSENT} />;
  // Resolved with no backend record — send them through onboarding to create one.
  if (authInitialized && !authLoading && firebaseUser && !firebaseUser.isAnonymous && userResolved)
    return <Navigate to={ROUTE_PATHS.ONBOARDING} />;

  return (
    <div className={className}>
      <InAppBrowserNotice />
      <h1>
        <Trans>Sign In</Trans> {additionalTitle}
      </h1>
      <form onSubmit={handleSubmit(onClick)}>
        <div>
          {signInForm === SIGN_IN_FORM.PHONE && (
            <>
              <PhoneField
                name="phone"
                control={control}
                setTouched={setPhoneInputTouched}
                touched={isPhoneTouched}
                error={errors.phone}
              />
              <p
                style={{
                  margin: 0,
                  position: 'relative',
                  top: '-10px',
                  fontSize: '14px',
                }}
              >
                <Trans>We'll text you to confirm your number. Standard message and data rates apply</Trans>
              </p>
            </>
          )}
          {showAltPrompt && (
            <p
              style={{
                fontSize: '16px',
                fontWeight: 'normal',
                marginBottom: '32px',
              }}
            >
              <Trans>
                We were unable to find an account with that email. If you signed up with your phone number, try logging
                in with your phone instead.
              </Trans>
            </p>
          )}
          {signInForm === SIGN_IN_FORM.EMAIL_PASSWORD && (
            <div className="mb-4 items-center w-full">
              <label className="text-sm font-medium whitespace-nowrap">
                <Trans>Email</Trans>
              </label>
              <div className="flex-1 min-w-0">
                <Input
                  autoFocus
                  className="w-full"
                  autoComplete="email"
                  testid={ids.emailField}
                  name="email"
                  type="email"
                  label={t`Email`}
                  {...register('email', { required: true, validate: validateEmail })}
                  enterKeyHint="next"
                />
                {errors?.email && <p className="text-red-500 text-sm mt-1">{emailError}</p>}
              </div>
            </div>
          )}
          {signInForm === SIGN_IN_FORM.EMAIL_PASSWORD && (
            <div className="mb-4 items-center w-full">
              <label className="text-sm font-medium whitespace-nowrap">
                <Trans>Password</Trans>
              </label>
              <div className="flex-1 min-w-0">
                <PasswordInput
                  className="w-full"
                  autoComplete="password"
                  testid={ids.password}
                  name="password"
                  label={t`Password`}
                  {...register('password', {
                    required: signInForm === SIGN_IN_FORM.EMAIL_PASSWORD && submitCount > 1,
                    minLength: 8,
                  })}
                  enterKeyHint="go"
                />
                {errors?.password && <p className="text-red-500 text-sm mt-1">{passwordError}</p>}
              </div>
            </div>
          )}
          <div
            style={{
              display: signInForm === SIGN_IN_FORM.PHONE ? 'inherit' : 'none',
            }}
            ref={containerRef}
            id="recaptcha-sign-in"
          ></div>

          <Button
            type="submit"
            variant="secondary"
            size="lg"
            className="w-full"
            disabled={
              disabled ||
              submitting ||
              requestOTP ||
              (!Capacitor.isNativePlatform() && !containerRef.current && signInForm === SIGN_IN_FORM.PHONE)
            }
          >
            {requestOTP ? (
              <Trans>Waiting for Code...</Trans>
            ) : submitting || disabled ? (
              <Trans>One Moment...</Trans>
            ) : (
              <Trans>Continue</Trans>
            )}
          </Button>
        </div>
        <span
          style={{
            display: 'flex',
            justifyContent: 'center',
            fontSize: '18px',
            margin: '4px 0',
          }}
        >
          <Trans>or</Trans>
        </span>
        <AppleButton onClick={() => handleSocialSignIn(handleAppleAuthSignIn)} isSignIn={true} />
        <GoogleButton onClick={() => handleSocialSignIn(handleGoogleAuthSignIn)} isSignIn={true} />
        {signInForm !== SIGN_IN_FORM.PHONE && (
          <PhoneButton
            onClick={() => {
              setSignInForm(SIGN_IN_FORM.PHONE);
            }}
            isSignIn={true}
          />
        )}
        {/* Email sign-in is enabled in staging and on Capacitor iOS */}
        {showEmailSignIn && signInForm !== SIGN_IN_FORM.EMAIL_PASSWORD && (
          <EmailButton onClick={() => setSignInForm(SIGN_IN_FORM.EMAIL_PASSWORD)} isSignIn={true} />
        )}
        {signInForm === SIGN_IN_FORM.EMAIL_PASSWORD && (
          <Link to={ROUTE_PATHS.FORGOT_PASSWORD} data-testid={ids.forgotPassword}>
            <Trans>Forgot password?</Trans>
          </Link>
        )}
      </form>

      <Modal open={showNoAccountModal} onClose={() => setShowNoAccountModal(false)}>
        <div className="flex flex-col gap-4">
          <h3 className="text-lg mb-2">
            <Trans>We couldn't find an account with that phone number. Would you like to create one?</Trans>
          </h3>
          <div className="flex flex-col gap-2">
            <Button variant="secondary" className="w-full" onClick={() => navigate(ROUTE_PATHS.REGISTER)}>
              <Trans>Create Account</Trans>
            </Button>
            <Button
              variant="outline"
              className="w-full font-normal bg-transparent shadow-none"
              onClick={() => setShowNoAccountModal(false)}
            >
              <Trans>Cancel</Trans>
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={errEccountExists} onClose={() => setError(null)}>
        <div className="p-2 flex flex-col w-full items-center gap-4">
          <h3>
            {error === AUTH_ERROR_CODES.GOOGLE_FAIL_EMAIL_PASSWORD ? (
              <Trans>
                An account exists with that email using email and password. Please sign in using your other credentials
              </Trans>
            ) : error === AUTH_ERROR_CODES.EMAIL_PASSWORD_FAIL_GOOGLE ? (
              <Trans>
                An account exists with that email using Google Sign In. Please sign in using your other credentials
              </Trans>
            ) : (
              <Trans>An account exists with that email. Please sign in using your other credentials</Trans>
            )}
          </h3>

          <Button variant="outline" className="text-black" onClick={() => setError(null)}>
            <Trans>Close</Trans>
          </Button>
        </div>
      </Modal>

      <Modal
        open={requestOTP}
        onClose={() => {
          setSubmitting(false);
          setRequestOTP(false);
          setDisabled(false);
        }}
      >
        <div className="flex flex-col gap-4">
          <h3 className="text-lg mb-2">
            <Trans>Enter the code we sent over SMS to {getValues('phone')}</Trans>
          </h3>
          <div className="mb-4">
            <RHFTextInput
              autoFocus
              name="otp"
              ariaLabel="SMS verification code"
              // @ts-ignore
              register={register}
              placeholder="- - - - - -"
              // iOS only offers the SMS code in the QuickType bar for fields marked
              // `one-time-code`; `type="number"` suppresses the suggestion, so keep it a text
              // field and get the numeric keypad from inputMode/pattern instead.
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              autoComplete="one-time-code"
              options={{
                required: true,
                minLength: 6,
                maxLength: 6,
              }}
              value={watchedOTP}
              label={''}
            />
            <button
              type="button"
              className={`text-sm mt-2 ${
                disabled || resendCountdown > 0 ? 'text-muted-foreground' : 'text-primary hover:underline'
              }`}
              disabled={disabled || resendCountdown > 0}
              onClick={async () => {
                try {
                  setDisabled(true);
                  const phone = getValues('phone');
                  await handleVerifyPhoneNumber(phone, true, false);
                  addMessage(t`A new code has been sent.`, true, 3000);
                  setResendCountdown(30);
                } catch (e) {
                  addMessage(t`Failed to resend code. Please try again.`, false, 5000);
                } finally {
                  setDisabled(false);
                }
              }}
            >
              {disabled
                ? t`Sending...`
                : resendCountdown > 0
                ? t`Resend in ${resendCountdown}s`
                : t`Didn't receive a code? Resend`}
            </button>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <Button
              variant="outline"
              type="button"
              className="w-full sm:w-auto font-normal bg-transparent shadow-none"
              onClick={() => {
                setSubmitting(false);
                setRequestOTP(false);
                setDisabled(false);
              }}
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              variant="secondary"
              className="w-full sm:w-auto"
              disabled={submitting || disabled}
              onClick={handlePhoneSubmit}
            >
              {submitting ? <Trans>Processing...</Trans> : <Trans>Submit</Trans>}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

/**
 * Styled version of component
 */
const StyledSignIn = styled(SignIn)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 860px;
  width: 400px;
  margin: 32px auto;
  @media ${({ theme }) => theme.mq.desktopMin} {
    margin-top: 100px;
    align-items: center;
  }
  && .MuiAlert-root {
    font-family: Majorant-Regular;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    max-width: 420px;
    margin: 0 auto;
  }

  h1 {
    font-family: Majorant-Medium;
    font-size: 30px;
    line-height: 32px;
    margin: 0 auto;
    margin-bottom: 24px;
    font-weight: 500;
    @media screen and ${({ theme }) => theme.mq.desktopSmallMin} {
      font-size: 40px;
      line-height: 46px;
      margin: 0 auto;
      margin-bottom: 24px;
    }
  }

  a {
    color: ${({ theme }) => theme.gray6};
    text-decoration: none;
  }

  & #recaptcha-sign-in {
    & > div {
      padding-bottom: 42px;
    }
  }
`;

/**
 * Default export is styled version
 */
export default StyledSignIn;
